import { Box, Typography, Paper, Grid, Chip, Alert, Button, Avatar, List, ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import { PlayArrow, Pause, Cancel, Done, Warning, CheckCircle, Schedule, PrivacyTip } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DRecord, WorkflowStatChip, SectionHeader } from './DashboardCard';

export const CriticalAlerts = ({ expiringCerts, overdueCompliance, pendingDSAR, expiringContracts }: { expiringCerts: number; overdueCompliance: number; pendingDSAR: number; expiringContracts: number }) => {
  const navigate = useNavigate();
  if (expiringCerts <= 0 && overdueCompliance <= 0 && pendingDSAR <= 0 && expiringContracts <= 0) return null;
  return (
    <Box sx={{ mb: 3 }}>
      {(expiringCerts > 0 || expiringContracts > 0) && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {expiringCerts > 0 && `${expiringCerts} OHS certification${expiringCerts > 1 ? 's' : ''} expiring within 30 days`}
          {expiringCerts > 0 && expiringContracts > 0 && ' · '}
          {expiringContracts > 0 && `${expiringContracts} contract${expiringContracts > 1 ? 's' : ''} expiring within 30 days`}
          {' — '}
          <Button size="small" onClick={() => navigate('/ohs-certifications')}>View OHS</Button>
          <Button size="small" onClick={() => navigate('/contract-alerts')}>View Contracts</Button>
        </Alert>
      )}
      {overdueCompliance > 0 && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {overdueCompliance} compliance deadline{overdueCompliance > 1 ? 's' : ''} overdue — immediate attention required
          <Button size="small" onClick={() => navigate('/compliance-calendar')}>View Calendar</Button>
        </Alert>
      )}
      {pendingDSAR > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {pendingDSAR} DSAR request{pendingDSAR > 1 ? 's' : ''} pending — POPIA requires response within 30 days
          <Button size="small" onClick={() => navigate('/dsar-requests')}>View DSAR</Button>
        </Alert>
      )}
    </Box>
  );
};

export const WorkflowStatus = ({ workflowInstances }: { workflowInstances: DRecord[] }) => {
  if (!workflowInstances || workflowInstances.length === 0) return null;
  return (
    <Paper sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Workflow Status</Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <WorkflowStatChip label="Active" value={workflowInstances.filter((w: DRecord) => w.status === 'active').length} icon={<PlayArrow sx={{ fontSize: 16 }} />} color="success" />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <WorkflowStatChip label="Paused" value={workflowInstances.filter((w: DRecord) => w.status === 'paused').length} icon={<Pause sx={{ fontSize: 16 }} />} color="warning" />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <WorkflowStatChip label="Completed" value={workflowInstances.filter((w: DRecord) => w.status === 'completed').length} icon={<Done sx={{ fontSize: 16 }} />} color="info" />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <WorkflowStatChip label="Cancelled" value={workflowInstances.filter((w: DRecord) => w.status === 'cancelled').length} icon={<Cancel sx={{ fontSize: 16 }} />} color="error" />
        </Grid>
      </Grid>
      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>By Type:</Typography>
        {['onboarding', 'disciplinarywarning', 'training', 'termination'].map(type => {
          const count = workflowInstances.filter((w: DRecord) => w.workflowType === type && w.status === 'active').length;
          if (count === 0) return null;
          return (
            <Chip key={type} label={`${type}: ${count}`} size="small" sx={{ mr: 1, textTransform: 'capitalize' }} color="primary" variant="outlined" />
          );
        })}
      </Box>
    </Paper>
  );
};

export const ExpiringCertifications = ({ certs, now, resolveEmpName }: { certs: DRecord[]; now: number; resolveEmpName: (ref: { id: string } | null | undefined) => string }) => (
  <Paper sx={{ p: 3 }}>
    <SectionHeader title="Expiring Certifications" actionHref="/ohs-certifications" actionLabel="View All" />
    {certs.length > 0 ? (
      <List dense>
        {certs.map((cert: DRecord) => {
          const expiry = new Date(cert.expiryDate as string);
          const days = Math.ceil((expiry.getTime() - now) / (1000 * 60 * 60 * 24));
          return (
            <ListItem key={cert.id} sx={{ px: 0 }}>
              <ListItemAvatar>
                <Avatar sx={{ width: 32, height: 32, bgcolor: days <= 14 ? 'error.main' : 'warning.main' }}>
                  <Warning fontSize="small" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={cert.certificationLevel}
                secondary={`${resolveEmpName(cert.employee)} — ${days}d`}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          );
        })}
      </List>
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
        <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">All certifications current</Typography>
      </Box>
    )}
  </Paper>
);

export const ContractAlertsList = ({ alerts, pendingAlerts, probationAlerts, fixedTermAlerts, now, resolveEmpName }: { alerts: DRecord[]; pendingAlerts: number; probationAlerts: number; fixedTermAlerts: number; now: number; resolveEmpName: (ref: { id: string } | null | undefined) => string }) => (
  <Paper sx={{ p: 3 }}>
    <SectionHeader title="Contract Alerts" actionHref="/contract-alerts" actionLabel="View All" />
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid size={{ xs: 6 }}>
        <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: probationAlerts > 0 ? 'warning.light' : 'action.hover' }}>
          <Typography variant="h5" fontWeight={700} color={probationAlerts > 0 ? 'warning.dark' : 'text.primary'}>{probationAlerts}</Typography>
          <Typography variant="caption" color="text.secondary">Probation</Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: fixedTermAlerts > 0 ? 'info.light' : 'action.hover' }}>
          <Typography variant="h5" fontWeight={700} color={fixedTermAlerts > 0 ? 'info.dark' : 'text.primary'}>{fixedTermAlerts}</Typography>
          <Typography variant="caption" color="text.secondary">Fixed Term</Typography>
        </Paper>
      </Grid>
    </Grid>
    {alerts.filter((a: DRecord) => a.status === 'Triggered').slice(0, 3).map((alert: DRecord) => {
      const trigger = new Date(alert.triggerDate as string);
      const days = Math.ceil((trigger.getTime() - now) / (1000 * 60 * 60 * 24));
      return (
        <ListItem key={alert.id} sx={{ px: 0 }}>
          <ListItemAvatar>
            <Avatar sx={{ width: 32, height: 32, bgcolor: days <= 14 ? 'error.main' : days <= 30 ? 'warning.main' : 'info.main' }}>
              <Schedule fontSize="small" />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={`${alert.alertType === 'ProbationExpiry' ? 'Probation' : 'Fixed Term'}: ${resolveEmpName(alert.employee)}`}
            secondary={`${alert.actionRequired?.substring(0, 40)}...`}
            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
          <Chip label={`${days}d`} size="small" color={days <= 14 ? 'error' : days <= 30 ? 'warning' : 'default'} />
        </ListItem>
      );
    })}
    {pendingAlerts === 0 && (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
        <CheckCircle sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">No pending alerts</Typography>
      </Box>
    )}
  </Paper>
);

export const ComplianceCalendar = ({ deadlines, overdueCompliance, now }: { deadlines: DRecord[]; overdueCompliance: number; now: number }) => (
  <Paper sx={{ p: 3 }}>
    <SectionHeader title="Compliance Calendar" actionHref="/compliance-calendar" actionLabel="View All" />
    {overdueCompliance > 0 && (
      <Alert severity="error" sx={{ mb: 2 }}>
        {overdueCompliance} deadline{overdueCompliance > 1 ? 's' : ''} overdue
      </Alert>
    )}
    {deadlines.map((item: DRecord) => {
      const due = new Date(item.dueDate as string);
      const days = Math.ceil((due.getTime() - now) / (1000 * 60 * 60 * 24));
      return (
        <ListItem key={item.id} sx={{ px: 0 }}>
          <ListItemAvatar>
            <Avatar sx={{ width: 32, height: 32, bgcolor: days <= 7 ? 'error.light' : days <= 30 ? 'warning.light' : 'info.light' }}>
              <Schedule fontSize="small" />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={item.submission}
            secondary={item.legislation}
            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
          <Chip label={`${days}d`} size="small" color={days <= 7 ? 'error' : days <= 30 ? 'warning' : 'default'} />
        </ListItem>
      );
    })}
    {deadlines.length === 0 && overdueCompliance === 0 && (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
        <CheckCircle sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">No upcoming deadlines</Typography>
      </Box>
    )}
  </Paper>
);
